import { ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { TooltipWrapper } from 'components/TooltipWrapper';
import type { ContractSeasonData, UercCalculationSeasonData } from 'gql/graphql';
import { GridItem, IconAlert, IconQuestion, Informer, Text } from 'uikit';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import { memo, useEffect, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import Styles from './otherManualSeasonDistribution.module.css';

type TOtherManualSeasonDistributionModal = {
    isOpened: boolean;
    isDisabled: boolean;
    toggleModalHandler: (isSubmit?: boolean) => void;
    costRounding: number;
    constructionTypeName: string;
    constructionTypeId: number;
    informerMessage?: string;
    tooltipMessage?: string;
    currentCost: string | undefined;
    seasons: (ContractSeasonData | UercCalculationSeasonData)[];
    control: Control<any>;
    isReadOnly?: boolean;
};

export const OtherManualSeasonDistributionModal = memo(
    ({
        isOpened,
        toggleModalHandler,
        currentCost,
        seasons,
        constructionTypeName,
        constructionTypeId,
        informerMessage = 'Сумма лимитов должна быть равна Лимиту по Договору',
        tooltipMessage = 'Сумма лимитов/Лимит по Договору',
        control,
        isDisabled,
        isReadOnly = false,
        costRounding,
    }: TOtherManualSeasonDistributionModal) => {
        const [informerVisible, setInformerVisible] = useState(false);

        const { manualDistribution: manualDistributionValues } = useWatch<{
            manualDistribution: Record<string, string>[];
        }>({
            control,
        });

        const currentConstructionTypeSeasonsSum = Object.values(
            manualDistributionValues?.[constructionTypeId] ?? {},
        )?.reduce((acc, item) => acc + replacerCost(item ?? 0), 0);

        const isWarningCostStyle = currentConstructionTypeSeasonsSum !== replacerCost(currentCost ?? 0);

        const submitDistributionHandler = () => {
            if (isWarningCostStyle) {
                setInformerVisible(true);

                return;
            }

            toggleModalHandler(true);
        };

        useEffect(() => {
            if (isWarningCostStyle) {
                setInformerVisible(true);
            } else {
                setInformerVisible(false);
            }
        }, [isWarningCostStyle]);

        return (
            <ModalDialog
                isOpen={isOpened}
                title="Способ распределения"
                bodyWidth={620}
                bodyMinHeight="300px"
                onClose={toggleModalHandler}
                onConfirm={submitDistributionHandler}
                isOnClickOutsideDisable
                withTitleCloseButton
                btnConfirmDisabled={informerVisible}
            >
                <div className={Styles.body}>
                    {informerVisible && !isReadOnly && (
                        <Informer
                            status="alert"
                            label={informerMessage}
                            icon={IconAlert}
                            className={Styles.distributionInformer}
                        />
                    )}

                    <Informer
                        status="system"
                        className={Styles.distributionSumInformer}
                        label={
                            <div className={Styles.distributionSumInformerBody}>
                                <Text view="secondary">{constructionTypeName}</Text>

                                <div className={Styles.distributionSum}>
                                    <Text view={isWarningCostStyle ? 'alert' : 'primary'} weight="semibold">
                                        {formatNumberToRu(currentConstructionTypeSeasonsSum, costRounding)} ₽ /&nbsp;
                                    </Text>
                                    <Text view="secondary">
                                        {formatNumberToRu(replacerCost(currentCost), costRounding)} ₽
                                    </Text>
                                    <TooltipWrapper
                                        message={tooltipMessage}
                                        className={Styles.distributionQuestion}
                                        toolTipClassName={Styles.distributionQuestionTooltip}
                                        isInteractive={false}
                                    >
                                        <IconQuestion size="s" view="secondary" />
                                    </TooltipWrapper>
                                </div>
                            </div>
                        }
                    />

                    <GridItem className={Styles.titles}>
                        <Text view="secondary">Сезон</Text>
                        <Text view="secondary">Сумма, руб. без НДС</Text>
                    </GridItem>

                    {seasons?.map((s) => (
                        <GridItem key={'id' in s ? s.id : s.seasonId} className={Styles.seasonInput}>
                            <ControlNumberField
                                name={`manualDistribution.${constructionTypeId}.season_${
                                    'id' in s ? s.id : s.seasonId
                                }`}
                                label={s.year}
                                type="text"
                                control={control}
                                defaultValue={formatNumberToRu(0, costRounding)}
                                decimalScale={costRounding}
                                fixedDecimalScale
                                labelPosition="left"
                                clearOnDisabled
                                disabled={isDisabled}
                            />
                        </GridItem>
                    ))}
                </div>
            </ModalDialog>
        );
    },
);

OtherManualSeasonDistributionModal.displayName = 'OtherManualSeasonDistributionModal';
